import { Button, ContextMenu, Icon, Li } from "@startlibs/components"
import { usePopupToggle } from "@startlibs/core"
import { TextInput, ToggleCheckbox } from "@startlibs/form"
import { getColor } from "@startlibs/utils"
import { darken } from "polished"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled, { css } from "styled-components"
import { DATE, getIconFromType, getTypeLabelFromType, SELECT, TEXT_AREA } from "../../admin/controlPanel/intakeUtils"
import { AddButton } from "../AddButton"

const ParagraphPlaceholder = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2; 
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FieldWrapper = styled.div`
    position: relative;
    margin-bottom: 1rem;
    text-align: left;
    width: 100%;
    flex: 1;
    min-width: 240px;

    ${props => props.hovered && css`
        ::before {
            content: "";
            position: absolute;
            top: -0.75rem;
            bottom: -0.75rem;
            left: -0.75rem; /* Extend beyond the left */
            right: -0.75rem; /* Extend beyond the right */
            background: ${getColor('gray240')};
            border-radius: 6px;
            z-index: -1; /* Send it behind the component */
        }
        cursor: pointer;
    `}
    ${props => props.editing && css`
        ::before {
            content: "";
            position: absolute;
            top: -0.75rem;
            bottom: -0.75rem;
            left: -0.75rem; /* Extend beyond the left */
            right: -0.75rem; /* Extend beyond the right */
            background: ${getColor('gray240')};
            border-radius: 6px;
            z-index: -1; /* Send it behind the component */
        }
    `}
`

const Label = styled.label`
    display: inline-block;
    font-size: 14px;
    font-weight: 600;
`
const FieldTypeLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
`

const FieldHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
`
const FieldTypeIcon = styled(Icon)`
    font-size: 25px;
    width: 2.5rem;
    margin-right: 0.25rem;
    text-align: center;
`

const ClearSearchButton = styled(Icon)`
  color: ${getColor('gray90')};
  font-size: 12px;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  width: 1.5rem;
  line-height: 1.5rem;
  text-align: center;
  border-radius: 50%;
  background: rgba(0,0,0,0.1);
  :hover {
    background: #f7d3d4;
    color: ${getColor('alert')};
  }
`

const Field = styled.div`
    width: 100%;
    height: 36px;
    ${props => props.isParagraph && css`
        height: 60px;
    `}
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='black' stroke-width='1' stroke-dasharray='3%2c 5' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
    border-radius: 5px;
    padding: 0.7rem;
    font-size: 13px;
    color: ${getColor('gray120')};
`

const IconButton = styled(Icon)`
    display: inline-block;
    position: relative;
    cursor: pointer;
    padding: 0.5rem 1rem;
    background-color: rgba(210,210,210,0.7);
    border-radius: 6px;
    color: ${getColor('gray90')};
    margin-bottom: 4px;
    :hover {
        background-color: ${getColor('gray210')};
        color: ${getColor('gray90')};
    }
`
const DragIcon = styled(Icon)`
    cursor: move;
    position: absolute;
    top: -6px;
    font-size: 15px;
    left: 50%;
    transform: translateX(-50%);
    // background: ${getColor('gray240')};
    border-radius: 6px;
    color:${getColor('gray90')};
    width: 30px;
    line-height: 16px;
    text-align: center;
    z-index: 10;
`

const RemoveItem = styled.a`
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: ${getColor('alert')};
    text-align: center;
    :hover {
        color: ${props => darken(0.1, getColor('alert')(props))};
    }
`

const MandatoryStar = styled.span`
    color: #c3282d;
    margin-left: 1px;
    vertical-align: top;
    line-height: 100%;
    margin-top: -1px;
`
const findFieldById = (lines, fieldId) => {
    for (const line of lines) {
        if (line.left && line.left.id === fieldId) {
            return line.left;
        }
        if (line.right && line.right.id === fieldId) {
            return line.right;
        }
    }
    return null; // Return null if the field is not found
};

export const TextInputCustom = ({item, editingItem, setEditingItem, updateFieldInForm, selectedForm, dragHandleProps, removeFieldFromForm, tabLanguage, defaultLanguage, fullWidth}) => {

    const [hovered, setHovered] = useState(false)
    const contextMenu = usePopupToggle()
    const isEditing = editingItem?.id === item.id

    useEffect(() => {        
        setHovered(false)
    }, [editingItem] )

    return isEditing ? (
        <EditingFieldWrapper 
            item={item} 
            editingItem={editingItem} 
            setEditingItem={setEditingItem}  
            updateFieldInForm={updateFieldInForm} 
            selectedForm={selectedForm} 
            removeFieldFromForm={removeFieldFromForm} 
            tabLanguage={tabLanguage}
            defaultLanguage={defaultLanguage}
        />
    ) : (
        <NonEditingFieldWrapper
            isParagraph={item.type === TEXT_AREA}
            item={item}
            contextMenu={contextMenu}
            setEditingItem={() => setEditingItem(item)}
            hovered={hovered}
            setHovered={setHovered}
            selectedForm={selectedForm}
            removeFieldFromForm={removeFieldFromForm}
            dragHandleProps={dragHandleProps}
            tabLanguage={tabLanguage}
            defaultLanguage={defaultLanguage}
            fullWidth={fullWidth}
            
        />
    );
}

function useLatest(value) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

const EditingFieldWrapper = ({
  item,
  editingItem,
  setEditingItem,
  updateFieldInForm,
  selectedForm,
  removeFieldFromForm,
  tabLanguage,
  defaultLanguage
}) => {

  const wrapperRef = useRef();

  const handleWrapperBlur = (e) => {
    if (!wrapperRef.current.contains(e.relatedTarget)) {
      // Focus left the entire wrapper → commit
      commitAllChanges();
    }
  };

  // Get the current field data
  const currentField = useMemo(() => {
    return findFieldById(selectedForm.isOpen.lines, editingItem.id) || {};
  }, [selectedForm.isOpen.lines, editingItem.id]);

  // Helper: get translated value from current field
  const getTranslatedField = useCallback(
  (fieldName) => {
    if (tabLanguage === defaultLanguage) {
      const value = currentField[fieldName];
      return value !== undefined && value !== null ? value : '';
    }

    const translation = currentField.translations?.find(
      (t) => t.language === tabLanguage
    );

    if (translation && Object.prototype.hasOwnProperty.call(translation, fieldName)) {
      return translation[fieldName];
    }

    const fallback = currentField[fieldName];
    return fallback !== undefined && fallback !== null ? fallback : '';
  },
  [currentField, tabLanguage, defaultLanguage]
);

  // ✅ Local states for inputs
  const [localName, setLocalName] = useState(getTranslatedField('name'));
  const [localDescription, setLocalDescription] = useState(
    getTranslatedField('description')
  );
   // Local options state
  const [localOptions, setLocalOptions] = useState(
    (tabLanguage === defaultLanguage
      ? currentField.options || []
      : currentField.translations?.find((t) => t.language === tabLanguage)
          ?.options || currentField.options || [])
  );

  // Update local states when the field or language changes (not on every currentField update)
  useEffect(() => {
    setLocalName(getTranslatedField('name'));
    setLocalDescription(getTranslatedField('description'));
  }, [editingItem.id, tabLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  const localNameRef = useLatest(localName);
  const localDescriptionRef = useLatest(localDescription);
  const localOptionsRef = useLatest(localOptions);

  useEffect(() => {
    localNameRef.current = localName;
  }, [localName]);

  useEffect(() => {
    localDescriptionRef.current = localDescription;
  }, [localDescription]);

  useEffect(() => {
    localOptionsRef.current = localOptions;
  }, [localOptions]);

  const handleRemove = useCallback(() => {
    removeFieldFromForm(selectedForm, editingItem.id);
    setEditingItem(null);
  }, [removeFieldFromForm, selectedForm, editingItem.id, setEditingItem]);

  const handleRequiredChange = useCallback(
    (value) => {
      updateFieldInForm(selectedForm, editingItem.id, { required: value });
    },
    [updateFieldInForm, selectedForm, editingItem.id]
  );

  const handleConfirm = () => {
    commitAllChanges();
    setEditingItem(null);
  };


  useEffect(() => {
    const opts =
      tabLanguage === defaultLanguage
        ? currentField.options || []
        : currentField.translations?.find((t) => t.language === tabLanguage)
            ?.options || currentField.options || [];
    setLocalOptions(opts);
  }, [currentField, tabLanguage, editingItem.id]);

  // Add option
  const handleAddOption = () => {
    const updatedOptions = [...localOptions, ''];
    setLocalOptions(updatedOptions);

    if (tabLanguage === defaultLanguage) {
      updateFieldInForm(selectedForm, editingItem.id, { options: updatedOptions });
    } else {
      const translations = currentField.translations || [];
      const updatedTranslations = translations.some(
        (t) => t.language === tabLanguage
      )
        ? translations.map((t) =>
            t.language === tabLanguage ? { ...t, options: updatedOptions } : t
          )
        : [...translations, { language: tabLanguage, options: updatedOptions }];

      updateFieldInForm(selectedForm, editingItem.id, {
        translations: updatedTranslations
      });
    }
  };

  // Remove option
  const handleRemoveOption = (index) => {
    const updatedOptions = localOptions.filter((_, i) => i !== index);
    setLocalOptions(updatedOptions);

    if (tabLanguage === defaultLanguage) {
      updateFieldInForm(selectedForm, editingItem.id, { options: updatedOptions });
    } else {
      const translations = currentField.translations || [];
      const updatedTranslations = translations.some(
        (t) => t.language === tabLanguage
      )
        ? translations.map((t) =>
            t.language === tabLanguage ? { ...t, options: updatedOptions } : t
          )
        : [...translations, { language: tabLanguage, options: updatedOptions }];

      updateFieldInForm(selectedForm, editingItem.id, {
        translations: updatedTranslations
      });
    }
  };

  const mergeTranslations = (currentTranslations, ...partialUpdates) => {
    const byLang = new Map();

    // Start from existing translations
    currentTranslations.forEach(t => {
      byLang.set(t.language, { ...t });
    });

    // Apply each partial update
    partialUpdates.flat().forEach(t => {
      const existing = byLang.get(t.language) || { language: t.language };
      byLang.set(t.language, { ...existing, ...t });
    });

    return Array.from(byLang.values());
  };

  const getTranslationUpdate = (fieldName, value) => {
    return [{ language: tabLanguage, [fieldName]: value }];
  };

  const commitAllChanges = () => {
    const name = localNameRef.current;
    const description = localDescriptionRef.current;
    const options = localOptionsRef.current;

    const currentTranslations = currentField.translations || [];

    const updatedTranslations = mergeTranslations(
      currentTranslations,
      getTranslationUpdate('name', name),
      getTranslationUpdate('description', description),
      getTranslationUpdate('options', options)
    );

    const baseUpdate = {
      translations: updatedTranslations
    };

    if (tabLanguage === defaultLanguage) {
      baseUpdate.name = name;
      baseUpdate.description = description;
      baseUpdate.options = options;
    }

    updateFieldInForm(selectedForm, editingItem.id, baseUpdate);
  };

  // validation: for SELECTs, we require at least one non‐empty option
  // const isSelectField    = item.type === SELECT;
  // const hasValidOptions  = localOptions.some(o => o.trim() !== '');

  return (
    <FieldWrapper 
      editing 
      ref={wrapperRef}
      onBlurCapture={handleWrapperBlur}
      onFocusCapture={() => {}}
    >
      <FieldHeader>
        <FieldTypeLabel>
          <FieldTypeIcon icon={getIconFromType(item.type)} />{' '}
          {getTypeLabelFromType(item.type)}
        </FieldTypeLabel>
        <RemoveItem onClick={handleRemove}>Remove field</RemoveItem>
      </FieldHeader>

      {/* Name and Required */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          marginBottom: '0.5rem'
        }}
      >
        <TextInput
          raw
          placeholder="Field title"
          value={localName}
          setValue={(v) => {setLocalName(v)}}
          mandatory
        />
        <ToggleCheckbox
          label={<b>Required</b>}
          raw
          value={currentField.required || false}
          setValue={handleRequiredChange}
          css="min-width: 124px; background-color: #dcdcdc; height: 36px; min-height: 36px; margin-left: 0.5rem; margin-bottom: 0px;"
        />
      </div>

      {/* Description */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          marginBottom: '0.5rem'
        }}
      >
        <TextInput
          raw
          placeholder="Field description (optional)"
          value={localDescription}
          setValue={setLocalDescription}
        />
        <Button
          onClick={handleConfirm}
          onlyIcon
          icon="check"
          highlight
          css="margin-left: 0.5rem;"
          // disabled={isSelectField && !hasValidOptions}
        />
      </div>

      {/* {isSelectField && !hasValidOptions && (
        <div style={{ color: '#c3282d', fontSize: '12px', marginBottom: '0.5rem' }}>
          ⚠️ Please add at least one option before saving.
        </div>
      )} */}

      {item.type === SELECT && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            gap: '0.5rem',
            marginBottom: '0.5rem'
          }}
        >
          <label style={{ fontSize: '14px' }}>
            <b>Options</b>
          </label>

          {localOptions.map((option, index) => (
            <div key={index} style={{ position: 'relative' }}>
              <TextInput
                raw
                value={option}
                setValue={(value) => {
                  const updated = [...localOptions];
                  updated[index] = value;
                  setLocalOptions(updated); // Local update while typing
                }}
              />
              <ClearSearchButton
                icon="x"
                onClick={() => handleRemoveOption(index)}
              />
            </div>
          ))}
          <AddButton onClick={handleAddOption}>Add option</AddButton>
        </div>
      )}
    </FieldWrapper>
  );
};


const NonEditingFieldWrapper = ({ item, contextMenu, setEditingItem, hovered, setHovered, selectedForm, removeFieldFromForm, isParagraph, dragHandleProps, tabLanguage, fullWidth }) => {

    const translation = item.translations?.find(t => t.language === tabLanguage);
    const name = translation?.name || item.name;
    // const description = translation?.description || item.description;
    const description = translation?.description !== undefined && translation?.description !== null
      ? translation.description
      : item.description;

    // validation: for SELECTs, we require at least one non‐empty option
    // const isSelectField    = item.type === SELECT;
    // const hasValidOptions  = item.options.some(o => o.trim() !== '');

    return (<FieldWrapper 
        isParagraph={isParagraph} 
        hovered={hovered}
        onMouseEnter={() => setHovered(true)} 
        onMouseLeave={() => setHovered(false)}
        onClick={() => setEditingItem(item)}
      >
        <FieldHeader>
            <Label>{name}{item.required && <MandatoryStar>*</MandatoryStar>}</Label>
            {hovered && <DragIcon icon='draggable' {...dragHandleProps} />}
            <IconButton icon='arrow-down' onClick={contextMenu.open}>
                {contextMenu.isOpen && (
                    <ContextMenu>
                        <Li label="Edit field" icon='edit' onClick={() => setEditingItem(item)} />
                        <Li label="Remove field" icon='delete' onClick={() => removeFieldFromForm(selectedForm, item.id)} />
                    </ContextMenu>
                )}
            </IconButton>
        </FieldHeader>
        <Field isParagraph={isParagraph}>
          {isParagraph && description && <ParagraphPlaceholder>{description ? description : ''}</ParagraphPlaceholder>}
          {!isParagraph && description && <div style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: fullWidth ? "600px" : "280px"}}>{description ? description : ''}</div>}
          {item?.type === SELECT && <Icon icon="arrow-down" style={{ position: "absolute", top: "45px", right: "12px"}} />}
          {item?.type === DATE && <Icon icon="calendar" style={{fontSize: "22px", position: "absolute", bottom: "8px", right: "10px"}} />}
        </Field>
        {/* {isSelectField && !hasValidOptions && (
          <div style={{ color: '#c3282d', fontSize: '12px', marginBottom: '0.5rem' }}>
            ⚠️ Please add at least one option before saving.
          </div>
        )} */}
    </FieldWrapper>
)}